import axios from 'axios'
import TripsApi from 'api/entities/TripsApi'
import UsersApi from 'api/entities/UsersApi'
import MembersApi from 'api/entities/MembersApi'
import MiscApi from 'api/entities/MiscApi'
import ProgramsApi from 'api/entities/ProgramsApi'
import TeamsApi from 'api/entities/TeamsApi'
import InvitesApi from 'api/entities/InvitesApi'
import SubscriptionsApi from 'api/entities/SubscriptionsApi'
import ExportsApi from 'api/entities/ExportsApi'
import ReportsApi from 'api/entities/ReportsApi'
import ComplianceApi from 'api/entities/ComplianceApi'
import TransactionsApi from 'api/entities/TransactionsApi'
import SchedulesApi from 'api/entities/SchedulesApi'
import PayPeriodsApi from 'api/entities/PayPeriodsApi'
import TagsApi from 'api/entities/TagsApi'
import PlacesApi from 'api/entities/PlacesApi'
import UnitApi from 'api/entities/UnitApi'
import RulesApi from 'api/entities/RulesApi'
import IntegrationsApi from 'api/entities/IntegrationsApi'
import MotusApi from 'api/entities/MotusApi'
import EverlanceApi from 'api/entities/EverlanceApi'
import { isMotusEnv } from 'app/settings/defaultSetting'
import { createAxiosInstanceWithName, setUpInterceptors } from './ApiUtils'
import SalesApi from 'api/entities/SalesApi'
import CatalogApi from './entities/CatalogApi'

const X_API_KEY = isMotusEnv
  ? import.meta.env.VITE_APP_MOTUS_X_API_KEY
  : import.meta.env.VITE_APP_X_API_KEY

const defaultConfig = {
  withCredentials: true,
  baseURL: import.meta.env.VITE_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Api-Key': X_API_KEY,
    accept: 'application/json'
  }
}
const motusConfig = {
  withCredentials: false,
  baseURL: import.meta.env.VITE_APP_MOTUS_API_BASE_URL
}

const noVersionConfig = {
  withCredentials: true,
  baseURL: import.meta.env.VITE_APP_API_BASE_URL.replace('v6/', ''),
  headers: {
    'Content-Type': 'application/json',
    'X-Api-Key': X_API_KEY,
    accept: 'application/json'
  }
}

class API {
  public motusAxiosInstance = axios.create(motusConfig)
  public everlanceAxiosInstance = axios.create(defaultConfig)
  public axiosInstance = axios.create(defaultConfig)
  public axiosInstanceNoVersion = axios.create(noVersionConfig)
  public defaultAxiosInstance = axios.create({
    ...defaultConfig,
    withCredentials: false
  })
  // Entities
  trips: TripsApi
  users: UsersApi
  motus: MotusApi
  everlance: EverlanceApi
  members: MembersApi
  misc: MiscApi
  programs: ProgramsApi
  teams: TeamsApi
  invites: InvitesApi
  subscriptions: SubscriptionsApi
  exports: ExportsApi
  reports: ReportsApi
  compliance: ComplianceApi
  transactions: TransactionsApi
  schedules: SchedulesApi
  payPeriods: PayPeriodsApi
  tags: TagsApi
  places: PlacesApi
  unit: UnitApi
  rules: RulesApi
  integrations: IntegrationsApi
  sales: SalesApi
  catalog: CatalogApi

  constructor() {
    // Setup request & response interceptors
    setUpInterceptors({
      instance: this.axiosInstance,
      interceptRequest: true
    })
    setUpInterceptors({
      instance: this.axiosInstanceNoVersion,
      interceptRequest: true,
      skipVersion: true
    })
    setUpInterceptors({
      instance: this.motusAxiosInstance,
      interceptRequest: true
    })

    // Setup response interceptors
    setUpInterceptors({
      instance: this.everlanceAxiosInstance
    })
    setUpInterceptors({
      instance: this.defaultAxiosInstance
    })

    // Company specific http requests - no interceptors
    this.everlance = new EverlanceApi(
      createAxiosInstanceWithName(this.everlanceAxiosInstance, 'EverlanceApi')
    )
    this.motus = new MotusApi(
      createAxiosInstanceWithName(this.motusAxiosInstance, 'MotusApi')
    )

    this.trips = new TripsApi(
      createAxiosInstanceWithName(this.axiosInstance, 'TripsApi'),
      createAxiosInstanceWithName(this.defaultAxiosInstance, 'TripsApi')
    )
    this.users = new UsersApi(
      createAxiosInstanceWithName(this.axiosInstance, 'UsersApi')
    )
    this.members = new MembersApi(
      createAxiosInstanceWithName(this.axiosInstance, 'MembersApi')
    )
    this.misc = new MiscApi(
      createAxiosInstanceWithName(this.axiosInstance, 'MiscApi'),
      createAxiosInstanceWithName(this.axiosInstanceNoVersion, 'MiscApi')
    )
    this.programs = new ProgramsApi(
      createAxiosInstanceWithName(this.axiosInstance, 'ProgramsApi')
    )
    this.teams = new TeamsApi(
      createAxiosInstanceWithName(this.axiosInstance, 'TeamsApi')
    )
    this.invites = new InvitesApi(
      createAxiosInstanceWithName(this.axiosInstance, 'InvitesApi')
    )
    this.subscriptions = new SubscriptionsApi(
      createAxiosInstanceWithName(this.axiosInstance, 'SubscriptionsApi')
    )
    this.exports = new ExportsApi(
      createAxiosInstanceWithName(this.axiosInstance, 'ExportsApi')
    )
    this.reports = new ReportsApi(
      createAxiosInstanceWithName(this.axiosInstance, 'ReportsApi')
    )
    this.compliance = new ComplianceApi(
      createAxiosInstanceWithName(this.axiosInstance, 'ComplianceApi')
    )
    this.transactions = new TransactionsApi(
      createAxiosInstanceWithName(this.axiosInstance, 'TransactionsApi')
    )
    this.schedules = new SchedulesApi(
      createAxiosInstanceWithName(this.axiosInstance, 'SchedulesApi')
    )
    this.payPeriods = new PayPeriodsApi(
      createAxiosInstanceWithName(this.axiosInstance, 'PayPeriodsApi')
    )
    this.tags = new TagsApi(
      createAxiosInstanceWithName(this.axiosInstance, 'TagsApi')
    )
    this.places = new PlacesApi(
      createAxiosInstanceWithName(this.axiosInstance, 'PlacesApi')
    )
    this.unit = new UnitApi(
      createAxiosInstanceWithName(this.axiosInstance, 'UnitApi')
    )
    this.rules = new RulesApi(
      createAxiosInstanceWithName(this.axiosInstance, 'RulesApi')
    )
    this.integrations = new IntegrationsApi(
      createAxiosInstanceWithName(this.axiosInstance, 'IntegrationsApi')
    )
    this.sales = new SalesApi(
      createAxiosInstanceWithName(this.axiosInstance, 'SalesApi')
    )
    this.catalog = new CatalogApi(
      createAxiosInstanceWithName(this.axiosInstanceNoVersion, 'CatalogApi')
    )
  }
}

const APIService = new API()
export default APIService
